import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import UpgradeModal from '../components/upgrade/UpgradeModal';

const UpgradeContext = createContext(null);

const DEFAULT_PERMISSIONS = {
  tier: 'free',
  max_visions: 3,
  can_connect_accounts: false,
  can_access_full_ai: false,
  can_access_strategy_card: false,
  can_follow_advisors: true,
  can_book_advisor: false,
  can_share_vision_card: false,
  active_vision_count: 0,
  at_vision_limit: false,
};

export function UpgradeProvider({ user, children }) {
  const [permissions, setPermissions] = useState(null);
  const [modalFeature, setModalFeature] = useState(null);

  const refreshPermissions = useCallback(async () => {
    if (!user?.user_id) {
      setPermissions(null);
      return;
    }
    try {
      const res = await axios.get('/api/user/permissions');
      setPermissions(res.data);
    } catch {
      setPermissions(DEFAULT_PERMISSIONS);
    }
  }, [user?.user_id]);

  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  const openUpgrade = useCallback((featureBlocked) => {
    setModalFeature(featureBlocked);
  }, []);

  const closeUpgrade = useCallback(() => {
    setModalFeature(null);
  }, []);

  const value = useMemo(
    () => ({
      permissions: permissions || DEFAULT_PERMISSIONS,
      refreshPermissions,
      openUpgrade,
      closeUpgrade,
      isPro: (permissions || DEFAULT_PERMISSIONS).tier === 'pro' || (permissions || DEFAULT_PERMISSIONS).tier === 'pro_plus',
    }),
    [permissions, refreshPermissions, openUpgrade, closeUpgrade],
  );

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      {modalFeature && (
        <UpgradeModal feature_blocked={modalFeature} on_close={closeUpgrade} />
      )}
    </UpgradeContext.Provider>
  );
}

export function useUpgrade() {
  const ctx = useContext(UpgradeContext);
  if (!ctx) {
    return {
      permissions: DEFAULT_PERMISSIONS,
      openUpgrade: () => {},
      closeUpgrade: () => {},
      refreshPermissions: async () => {},
      isPro: false,
    };
  }
  return ctx;
}

export default UpgradeContext;
